import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Loader2, Check, Copy, X, FileDown, Link2 } from "lucide-react";
import axios from "axios";
import { API, authHeaders } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * EmailShareDialog - lightweight modal to share the report via:
 *   - a `mailto:` link pre-filled with the share URL (no API key needed)
 *   - copy raw link
 *   - direct PDF download
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - reportId: string
 */
export function EmailShareDialog({ open, onClose, reportId }) {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLink = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/reports/${reportId}/share`,
        {},
        { headers: authHeaders() }
      );
      setToken(res.data.token);
      return res.data.token;
    } catch {
      toast.error("Nie udało się wygenerować linku");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const buildShareUrl = (t) => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/share/${t}`;
  };

  const sendMail = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Podaj poprawny email");
      return;
    }
    const t = token || (await generateLink());
    if (!t) return;
    const url = buildShareUrl(t);
    const subject = encodeURIComponent("Twój raport AI Dating Coach");
    const body = encodeURIComponent(
      [
        "Cześć,",
        "",
        "Oto Twój raport z analizy AI Dating Coach:",
        url,
        "",
        "Link jest ważny przez 7 dni.",
        "",
        "—",
        "AI Dating Coach",
      ].join("\n")
    );
    window.location.href = `mailto:${encodeURIComponent(email)}?subject=${subject}&body=${body}`;
    toast.success("Otwieram klient pocztowy...");
  };

  const copyLink = async () => {
    const t = token || (await generateLink());
    if (!t) return;
    const url = buildShareUrl(t);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Skopiowano link");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Skopiowanie nie powiodło się");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl border border-[#222636] bg-[#181b24] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            data-testid="email-share-dialog"
          >
            {/* Decorative gradient header */}
            <div className="relative h-24 overflow-hidden bg-gradient-to-br from-[#ff4458] via-[#fd267a] to-[#ff6036]">
              <div
                className="absolute inset-0 opacity-30 mix-blend-overlay"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
                  backgroundSize: "20px 20px",
                }}
              />
              <button
                type="button"
                onClick={onClose}
                data-testid="dialog-close"
                aria-label="Close"
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-md transition hover:bg-black/55"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-3 left-5 text-white">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                  AI Dating Coach
                </div>
                <h3 className="font-display text-xl font-extrabold">
                  Pobierz / udostępnij raport
                </h3>
              </div>
            </div>

            <div className="space-y-5 p-6">
              {/* Email -> mailto */}
              <div className="space-y-2">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#a8adbd]">
                  Wyślij linkiem na email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ty@example.com"
                    data-testid="email-share-input"
                    className="flex-1 rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-2.5 text-sm text-white outline-none placeholder:text-[#a8adbd]/40 focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/20"
                  />
                  <button
                    type="button"
                    onClick={sendMail}
                    disabled={loading}
                    data-testid="email-share-send"
                    className="inline-flex items-center gap-2 rounded-2xl dc-gradient-bg px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(253,38,122,0.3)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    Wyślij
                  </button>
                </div>
                <p className="text-[10.5px] text-[#a8adbd]">
                  Otworzy się Twój klient pocztowy z gotową wiadomością i linkiem (ważny 7 dni).
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="email-share-copy-link"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-[#ff4458]/40"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-[#32d583]" />
                  ) : (
                    <Link2 className="h-4 w-4 text-[#a8adbd]" />
                  )}
                  {copied ? "Skopiowane" : "Kopiuj link"}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const t = token || (await generateLink());
                    if (!t) return;
                    window.open(`${API}/share/${t}/pdf`, "_blank");
                  }}
                  data-testid="email-share-download-pdf"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-[#ff4458]/40"
                >
                  <FileDown className="h-4 w-4 text-[#a8adbd]" />
                  PDF
                </button>
              </div>

              {token && (
                <div className="rounded-2xl border border-[#32d583]/30 bg-[#32d583]/10 px-3 py-2 text-[11px] text-[#32d583]">
                  Link wygenerowany. Możesz wkleić go gdziekolwiek.
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
